import React from 'react';
import { View, StyleSheet, TouchableOpacity, Modal, Alert } from 'react-native';
import { Theme, ThemeConsumer } from '@rneui/themed';
import RNPrint from 'react-native-print';
import { ZenText } from 'zen-core-elements';
import { useAppSelector } from '../../../../redux/hooks';
import { generateReceiptHtml } from '../../../../utils/orderReceiptGenerator';

interface FinalOrderModalProps {
	isVisible: boolean;
	onCompleteSale: () => void; // Navigation logic goes here
}

const OrderSuccessModal: React.FC<FinalOrderModalProps> = ({ isVisible, onCompleteSale }) => {

	const { placeOrderApi } = useAppSelector((state) => state?.NewSellscreenPaymentSlice)

	const handlePrint = async () => {
		Alert.alert("Printer", "Sending receipt to printer...");
		const receiptHtml = generateReceiptHtml(placeOrderApi?.value);
		// Print directly via thermal printer
		await RNPrint.print({ html: receiptHtml });
	};


	const handleEmail = () => {
		// Logic to trigger email API
		Alert.alert("Email", "Digital receipt sent to customer.");
	};

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<Modal
						visible={isVisible}
						transparent={true}
						animationType="fade"
						onRequestClose={() => { }}
					>
						<View style={styles.overlay}>
							<View style={styles.modalCard}>

								{/* Success Header */}
								<View style={styles.header}>
									<View style={styles.iconCircle}>
										<ZenText content="✓" style={styles.checkIcon} />
									</View>
									<ZenText content="Transaction Completed" style={styles.statusTitle} />
									<ZenText content={`Order ID: ${placeOrderApi?.ordersId || "----"}`} style={styles.timestamp} />
								</View>

								{/* Receipt Options Section */}
								<View style={styles.optionsContainer}>
									<TouchableOpacity style={styles.optionButton} onPress={handlePrint}>
										<ZenText content="🖨️" style={styles.optionEmoji} />
										<ZenText content="Print Receipt" style={styles.optionText} />
									</TouchableOpacity>

									<TouchableOpacity style={styles.optionButton} onPress={handleEmail}>
										<ZenText content="📧" style={styles.optionEmoji} />
										<ZenText content="Email Receipt" style={styles.optionText} />
									</TouchableOpacity>
								</View>

								{/* Final Enforcement Button */}
								<View style={styles.footer}>
									<TouchableOpacity
										style={styles.completeButton}
										onPress={onCompleteSale}
									>
										<ZenText content="Start New Sale" style={styles.completeButtonText} />
									</TouchableOpacity>
									<ZenText content="This will clear the current cart and start a new sale." style={styles.disclaimer} />
								</View>

							</View>
						</View>
					</Modal>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.7)',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
	},
	modalCard: {
		width: '100%',
		maxWidth: 400,
		backgroundColor: 'white',
		borderRadius: 24,
		padding: 30,
		alignItems: 'center',
		shadowColor: "#000",
		shadowOffset: { width: 0, height: 10 },
		shadowOpacity: 0.3,
		shadowRadius: 20,
		elevation: 10,
	},

	header: { alignItems: 'center', marginBottom: 25 },
	iconCircle: { width: 64, height: 64, borderRadius: 32, backgroundColor: theme?.wildGreen, justifyContent: 'center', alignItems: 'center', marginBottom: 16 },
	checkIcon: { color: theme?.light, fontSize: 32, fontFamily: theme?.bold },
	statusTitle: { fontSize: theme?.headerFontSize, fontFamily: theme?.bold, color: theme?.lightDark },
	timestamp: { color: theme?.grey, fontSize: theme?.errorFontSize, fontFamily: theme?.medium, marginTop: 4 },

	optionsContainer: {
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 40,
		gap: 15,
	},
	optionButton: {
		flex: 1,
		borderWidth: 1,
		borderColor: theme?.coolGray,
		borderRadius: 16,
		padding: 20,
		alignItems: 'center',
		backgroundColor: theme?.iceWhite,
	},
	optionEmoji: {
		fontSize: theme?.headerFontSize,
		fontFamily: theme?.bold,
		marginBottom: 8,
	},
	optionText: {
		fontSize: theme?.errorFontSize,
		fontFamily: theme?.medium,
		color: theme?.lightDark,
	},
	footer: {
		width: '100%',
		alignItems: 'center',
	},
	completeButton: {
		backgroundColor: theme?.primary,
		width: '100%',
		padding: 20,
		borderRadius: 16,
		alignItems: 'center',
		marginBottom: 15,
	},
	completeButtonText: {
		color: theme?.light,
		fontSize: theme?.smallerFontSize,
		fontFamily: theme?.bold,
	},
	disclaimer: {
		fontSize: theme?.badgeFontSize,
		fontFamily: theme?.medium,
		color: theme?.grey,
		textAlign: 'center',
	},
});

export default OrderSuccessModal;